import { Dialog, DialogContent, DialogTitle, DialogDescription } from "./ui/dialog";
import { ScrollArea } from "./ui/scroll-area";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useUser } from "@/hooks/use-user";
import { useSessionAuth } from "@/hooks/use-session-auth";
import { toast } from "sonner";
import { X, ChevronLeft, ChevronRight, Maximize2 } from "lucide-react";
import { Button } from "./ui/button";
import { PostModalHeader } from "./posts/PostModalHeader";
import { PostModalImage } from "./posts/PostModalImage";
import { PostModalComments } from "./posts/PostModalComments";
import { PostInteractions } from "./posts/PostInteractions";
import { Link } from "react-router-dom";
import { Save } from "lucide-react";
import { SocialMetaTags } from "@/components/SocialMetaTags";
import { useIsMobile } from "@/hooks/use-mobile";
import { CarouselControl } from "./ui/carousel-control";
import { SaveToCollection } from "./SaveToCollection";

type Post = {
  id: string;
  title: string;
  description: string;
  image_url: string;
  category: string;
  created_at: string;
  profiles: {
    username: string;
    avatar_url: string;
    id: string;
  };
  user_has_liked?: boolean;
  likes_count?: number;
  comments_count?: number;
};

interface PostModalProps {
  post: Post | null;
  isOpen: boolean;
  onClose: () => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  onLike: (postId: string) => Promise<void>;
}

export function PostModal({
  post,
  isOpen,
  onClose,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
  onLike
}: PostModalProps) {
  const { user } = useUser();
  const isMobile = useIsMobile();
  const { userAuthenticated } = useSessionAuth();
  const [comments, setComments] = useState<any[]>([]);
  const [showImageModal, setShowImageModal] = useState(false);

  useEffect(() => {
    if (post) {
      fetchComments();
    }
  }, [post]);

  const fetchComments = async () => {
    if (!post) return;
    
    try {
      const { data, error } = await supabase
        .from("pin_comments")
        .select(`
          id,
          content,
          created_at,
          user_id,
          parent_id,
          profile:profiles(username, avatar_url, id)
        `)
        .eq("pin_id", post.id)
        .order("created_at", { ascending: true });

      if (error) throw error;

      const commentIds = data.map(comment => comment.id);
      
      const { data: likesData } = await supabase.rpc('count_comment_likes', {
        comment_ids: commentIds
      });

      const likesMap = new Map();
      if (likesData) {
        likesData.forEach((item: any) => {
          likesMap.set(item.comment_id, item.count);
        });
      }

      let userLikedComments = new Set();
      
      if (user) {
        const { data: userLikes } = await supabase
          .from("pin_comment_likes")
          .select("comment_id")
          .eq("user_id", user.id)
          .in("comment_id", commentIds);

        if (userLikes) {
          userLikedComments = new Set(userLikes.map(like => like.comment_id));
        }
      }

      const enhancedComments = data.map((comment: any) => ({
        ...comment,
        likes_count: likesMap.get(comment.id) || 0,
        user_has_liked: userLikedComments.has(comment.id)
      }));

      setComments(enhancedComments);
    } catch (error) {
      console.error("Error fetching comments:", error);
      toast.error("Erro ao carregar comentários");
    }
  };

  if (!post) return null;

  return (
    <>
      {isOpen && (
        <SocialMetaTags
          title={post.title || "InkWebtoons"}
          description={post.description || "Confira esta incrível arte no InkWebtoons"}
          imageUrl={post.image_url}
          url={`${window.location.origin}/pin/${post.id}`}
          contentType="pin"
          contentId={post.id}
        />
      )}
      
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`max-w-5xl p-0 gap-0 h-[90vh] overflow-hidden ${isMobile ? 'h-[95vh]' : 'h-[90vh]'}`}>
          <DialogTitle className="sr-only">Detalhes do Post</DialogTitle>
          <DialogDescription id="post-modal-description" className="sr-only">
            Visualização detalhada do post com comentários
          </DialogDescription>
          
          {/* Conteúdo do Modal */}
          {isMobile ? (
            // Layout mobile com scroll único para tudo
            <ScrollArea className="h-full w-full">
              <div className="flex flex-col h-full">
                {/* Imagem do post */}
                <div className="relative">
                  <img 
                    src={post.image_url} 
                    alt={post.title || "Imagem do post"} 
                    className="w-full object-contain max-h-[45vh]"
                  />
                  
                  {/* Botão de maximizar */}
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="absolute top-4 right-4 z-10 bg-white/50 hover:bg-white/80 dark:bg-black/50 dark:hover:bg-black/80 backdrop-blur-sm" 
                    onClick={() => setShowImageModal(true)}
                  >
                    <Maximize2 className="h-5 w-5 text-gray-700 dark:text-gray-200" />
                  </Button>
                  
                  {/* Botões de navegação */}
                  <div className="absolute inset-y-0 left-0 flex items-center pl-2">
                    {hasPrevious && (
                      <CarouselControl
                        type="previous"
                        title="Post anterior"
                        handleClick={onPrevious}
                        disabled={!hasPrevious}
                      />
                    )}
                  </div>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2">
                    {hasNext && (
                      <CarouselControl
                        type="next"
                        title="Próximo post"
                        handleClick={onNext}
                        disabled={!hasNext}
                      />
                    )}
                  </div>
                </div>
                
                {/* Informações e comentários */}
                <div className="p-6 pb-4">
                  <PostModalHeader 
                    profile={post.profiles}
                    title={post.title}
                    description={post.description}
                    category={post.category}
                    createdAt={post.created_at}
                  />
                
                  <PostInteractions 
                    onLikeToggle={() => onLike(post.id)}
                    isLiked={post.user_has_liked}
                    likesCount={post.likes_count || 0}
                    commentsCount={comments.length}
                    postId={post.id}
                    title={post.title}
                    imageUrl={post.image_url}
                  />
                  
                  {userAuthenticated ? (
                    <SaveToCollection postId={post.id} />
                  ) : (
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/auth">
                        <Save className="h-4 w-4 mr-2" />
                        Salvar
                      </Link>
                    </Button>
                  )}
                  
                  <PostModalComments 
                    pinId={post.id}
                    comments={comments}
                    user={user}
                    userAuthenticated={userAuthenticated}
                    onCommentAdded={fetchComments}
                  />
                </div>
              </div>
            </ScrollArea>
          ) : (
            // Layout desktop com divisão em duas colunas
            <div className="grid grid-cols-1 md:grid-cols-2 h-full overflow-hidden">
              {/* Coluna da imagem */}
              <PostModalImage 
                imageUrl={post.image_url}
                title={post.title || ""}
                hasPrevious={hasPrevious}
                hasNext={hasNext}
                onPrevious={onPrevious}
                onNext={onNext}
                onMaximize={() => setShowImageModal(true)}
              />

              {/* Coluna dos comentários e informações */}
              <ScrollArea className="flex flex-col h-full overflow-auto">
                <div className="p-6 pb-4">
                  <PostModalHeader 
                    profile={post.profiles}
                    title={post.title}
                    description={post.description}
                    category={post.category}
                    createdAt={post.created_at}
                  />
                
                  <PostInteractions 
                    onLikeToggle={() => onLike(post.id)}
                    isLiked={post.user_has_liked}
                    likesCount={post.likes_count || 0}
                    commentsCount={comments.length}
                    postId={post.id}
                    title={post.title}
                    imageUrl={post.image_url}
                  />
                  
                  {userAuthenticated ? (
                    <SaveToCollection postId={post.id} />
                  ) : (
                    <Button variant="outline" size="sm" asChild>
                      <Link to="/auth">
                        <Save className="h-4 w-4 mr-2" />
                        Salvar
                      </Link>
                    </Button>
                  )}
                  
                  <PostModalComments 
                    pinId={post.id}
                    comments={comments}
                    user={user}
                    userAuthenticated={userAuthenticated}
                    onCommentAdded={fetchComments}
                  />
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Único botão de fechar no canto superior direito */}
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-4 top-4 rounded-full bg-background/80 backdrop-blur-sm z-50" 
            onClick={onClose}
          >
            <X className="h-4 w-4" />
          </Button>
        </DialogContent>
      </Dialog>

      {/* Modal de imagem em tela cheia */}
      <Dialog open={showImageModal} onOpenChange={() => setShowImageModal(false)}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] p-0">
          <DialogTitle className="sr-only">Imagem em tela cheia</DialogTitle>
          <DialogDescription className="sr-only">
            Visualização em tela cheia da imagem do post
          </DialogDescription>
          <div className="relative w-full h-full flex items-center justify-center bg-black/90">
            <img 
              src={post.image_url} 
              alt={post.title || "Imagem do post"} 
              className="max-w-full max-h-[90vh] object-contain" 
            />
            <Button 
              variant="ghost" 
              size="icon" 
              className="absolute right-4 top-4 rounded-full bg-white/10 hover:bg-white/20" 
              onClick={() => setShowImageModal(false)}
            >
              <X className="h-4 w-4 text-white" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
